import { z } from "zod";

import { ActivityLevel, DietGoal, DietType } from "./diet";
import { createGymSchema, WEEKDAYS } from "./gyms";
import { athleteOnboardingSchema, trainingQuestionnaireSchema } from "./questionnaires";
import { EQUIPMENT_KEYS } from "./training";

/** What brought someone here. It decides what Home puts first, never whether they go through the first run. */
export enum FirstRunIntent {
  OWN = "own",
  CLIENT = "client",
  FIND = "find",
}

/**
 * The splits the first run offers. Recovery, upper/lower, push/pull/legs and full body are engine
 * splits; the rest become days on the plan, and custom opens the split editor once they are in.
 */
export enum FirstRunSplit {
  RECOVERY = "recovery",
  CUSTOM = "custom",
  PUSH_PULL_LEGS_FULL_BODY = "push_pull_legs_full_body",
  UPPER_LOWER = "upper_lower",
  PUSH_PULL_LEGS = "push_pull_legs",
  PUSH_PULL_LEGS_UPPER_LOWER = "push_pull_legs_upper_lower",
  FULL_BODY = "full_body",
  BODY_PART = "body_part",
}

/** One gym picked in the first run, the main one first. Equipment only when we do not know the gym's. */
export const firstRunGymSchema = z.object({
  gym: createGymSchema,
  equipmentKeys: z.array(z.enum(EQUIPMENT_KEYS)).max(EQUIPMENT_KEYS.length).optional(),
});
export type FirstRunGym = z.infer<typeof firstRunGymSchema>;

export const firstRunFoodSchema = z.object({
  goal: z.enum(DietGoal),
  weeklyRateKg: z.number().min(0).max(1),
  activity: z.enum(ActivityLevel),
  dietType: z.enum(DietType),
});
export type FirstRunFood = z.infer<typeof firstRunFoodSchema>;

/** Everything the finished first run hands over. The engines read it from where it is saved. */
export const firstRunCompleteSchema = z.object({
  intent: z.enum(FirstRunIntent),
  profile: athleteOnboardingSchema,
  training: trainingQuestionnaireSchema,
  split: z.enum(FirstRunSplit),
  gyms: z.array(firstRunGymSchema).min(1).max(5),
  /** Null when they chose training only. */
  food: firstRunFoodSchema.nullable(),
});
export type FirstRunComplete = z.infer<typeof firstRunCompleteSchema>;

/**
 * Answers while the first run is under way, so a reload or another device resumes. Deliberately
 * loose: it is only ever read back by the same flow, and finishing validates everything.
 */
export const firstRunDraftSchema = z.object({
  step: z.string().max(80),
  answers: z.record(z.string(), z.unknown()),
});
export type FirstRunDraft = z.infer<typeof firstRunDraftSchema>;

export const firstRunWeekdays = WEEKDAYS;

/** Where a paywall opens (ADR 0007). Each is measured on its own. */
export enum PaywallPlacement {
  ATHLETE_YEAR_OFFER = "athlete_year_offer",
  ATHLETE_ONBOARDING = "athlete_onboarding",
  ATHLETE_WORKOUT_LIMIT = "athlete_workout_limit",
  ATHLETE_MEAL_SCAN_LIMIT = "athlete_meal_scan_limit",
}

export enum PlatformPlanInterval {
  MONTH = "month",
  YEAR = "year",
}

/** Keys are stored, so never rename one. Names shown to people are copy. */
export const ATHLETE_FREE_PLAN = "athlete_free";
export const ATHLETE_PAID_PLAN = "athlete_paid";

export enum PlatformPlanLimit {
  LOGGED_WORKOUTS = "logged_workouts",
  MEAL_SCANS = "meal_scans",
}

export const athleteCheckoutSchema = z.object({
  interval: z.enum(PlatformPlanInterval),
  /** The one-time first year at 25% off. Refused once the offer has passed. */
  yearOffer: z.boolean().default(false),
});
